import { inject, signal } from '@angular/core';
import { Subject, Observable, map, merge, shareReplay, of, takeUntil, take, filter, concat, skipUntil, switchMap, groupBy, mergeMap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { Result, ok, err } from '@hereisyourgitanton/core';
import { exhaustiveCheck } from '@tradgine/utils';
import { SealedLotStore, SealedLotRef } from '../store/sealed-lot.store';
import {
    SealedTradingSnapshot,
    SealedTradingProcedure,
    SealedAuction,
    UpsertSealedBidPayload,
    SealedBid,
    SealedOffer,
    ParticipantRole,
    SealedTradingProcedureDocument,
    SealedTradingProcedureRegistry,
    TradingProcedureStatusChanged,
    ConnectionStatus,
} from '../domain/models';

const SELLER_DEANON_STATUSES = ['AwaitingDecision', 'Finished', 'Cancelled'];
import { SEALED_TRADING_QUERY_TOKEN } from '../tokens/sealed-trading-query.token';
import { SEALED_TRADING_COMMAND_TOKEN } from '../tokens/sealed-trading-command.token';
import { SEALED_TRADING_EVENTS_TOKEN } from '../tokens/sealed-trading-events.token';
import {
    SealedBidEventPayload,
    SealedAuctionLifecyclePayload,
} from '../ports/sealed-trading-events.port';

export type RuntimeStatus = 'idle' | 'loading' | 'ok' | 'error';

export type InitLoadEvent = {
    kind: 'InitLoad';
    snapshot: SealedTradingSnapshot;
};

export type AuctionStartedEvent = {
    kind: 'AuctionStarted';
    payload: SealedAuctionLifecyclePayload;
};

export type AuctionEndedEvent = {
    kind: 'AuctionEnded';
    payload: SealedAuctionLifecyclePayload;
};

export type BidPlacedEvent = {
    kind: 'BidPlaced';
    payload: SealedBidEventPayload;
};

export type BidUpdatedEvent = {
    kind: 'BidUpdated';
    payload: SealedBidEventPayload;
};

export type BidDeletedEvent = {
    kind: 'BidDeleted';
    payload: SealedBidEventPayload;
};

type SealedTPEvent =
    | InitLoadEvent
    | AuctionStartedEvent
    | AuctionEndedEvent
    | BidPlacedEvent
    | BidUpdatedEvent
    | BidDeletedEvent;

export class SealedTradingUseCase {
    private readonly queryPort = inject(SEALED_TRADING_QUERY_TOKEN);
    private readonly commandPort = inject(SEALED_TRADING_COMMAND_TOKEN);
    private readonly eventsPort = inject(SEALED_TRADING_EVENTS_TOKEN);

    private readonly store = new SealedLotStore();
    private readonly lotIdByAuctionId = new Map<number, number>();
    getAuctionIdByLotId(lotId: number): number | null {
        for (const [auctionId, mappedLotId] of this.lotIdByAuctionId.entries()) {
            if (mappedLotId === lotId) {
                return auctionId;
            }
        }
        return null;
    }

    private readonly destroy$ = new Subject<void>();

    // Событие смены статуса ТП может прийти по сокету до того, как снапшот загружен.
    // Сохраняем его здесь и применяем сразу после onInitLoad.
    private _pendingStatusEvent: TradingProcedureStatusChanged | null = null;

    // Триггеры для перезапроса аукциона и ставки при получении сокет-событий.
    // switchMap per auctionId гарантирует, что применяется только последний HTTP-ответ —
    // при быстрых событиях подряд предыдущий запрос отменяется.
    private readonly _auctionRefresh$ = new Subject<number>();
    private readonly _myBidRefresh$ = new Subject<number>();

    // leadingSealedBidId из события auctionEnded надёжнее, чем из последующего getAuctionById —
    // бэк может вернуть null если запрос обогнал запись в БД. Сохраняем до момента применения.
    private readonly _pendingLeadingBidId = new Map<number, number>();

    private readonly _status = signal<RuntimeStatus>('idle');
    get status() {
        return this._status.asReadonly();
    }

    private readonly _organizationId = signal<number | null>(null);
    get organizationId() {
        return this._organizationId.asReadonly();
    }

    private readonly _role = signal<ParticipantRole | null>(null);
    get role() {
        return this._role.asReadonly();
    }

    private readonly _offersByAuctionId = signal<Map<number, SealedOffer[]>>(new Map());
    get offersByAuctionId() {
        return this._offersByAuctionId.asReadonly();
    }

    private readonly _organizationNameById = signal<Record<number, string>>({});
    get organizationNameById() {
        return this._organizationNameById.asReadonly();
    }

    private readonly _connectionStatus = signal<ConnectionStatus>('connecting');
    get connectionStatus() {
        return this._connectionStatus.asReadonly();
    }

    private readonly _tradingProcedure = signal<SealedTradingProcedure | null>(null);
    get tradingProcedure() {
        return this._tradingProcedure.asReadonly();
    }

    private readonly _lotRefsById = signal<Map<number, SealedLotRef> | null>(null);
    get lotRefsById() {
        return this._lotRefsById.asReadonly();
    }

    init(tradingProcedureId: number): void {
        this._status.set('loading');

        const load$: Observable<Result<SealedTPEvent, Error>> = this.queryPort.getSnapshot(tradingProcedureId).pipe(
            map((result) =>
                result
                    .map((snapshot) => ({ kind: 'InitLoad', snapshot } as InitLoadEvent))
                    .mapErr((err) => new Error($localize`:@@sealed-auction.error.load-snapshot:Failed to load sealed trading procedure data: ${err.message}:originalError:${err.message}`))
            ),
            take(1),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        const ready$ = load$.pipe(
            filter((r) => r.isOk()),
            take(1),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        const socketEvents$: Observable<Result<SealedTPEvent, Error>> = merge(
            this.eventsPort.auctionStarted$.pipe(
                filter((ev) => this.store.refsByLotId.has(ev.lotId)),
                map((payload) => ok<AuctionStartedEvent, Error>({ kind: 'AuctionStarted', payload }))
            ),
            this.eventsPort.auctionEnded$.pipe(
                filter((ev) => this.store.refsByLotId.has(ev.lotId)),
                map((payload) => ok<AuctionEndedEvent, Error>({ kind: 'AuctionEnded', payload }))
            ),
            this.eventsPort.bidPlaced$.pipe(
                filter((ev) => this.lotIdByAuctionId.has(ev.auctionId)),
                map((payload) => ok<BidPlacedEvent, Error>({ kind: 'BidPlaced', payload }))
            ),
            this.eventsPort.bidUpdated$.pipe(
                filter((ev) => this.lotIdByAuctionId.has(ev.auctionId)),
                map((payload) => ok<BidUpdatedEvent, Error>({ kind: 'BidUpdated', payload }))
            ),
            this.eventsPort.bidDeleted$.pipe(
                filter((ev) => this.lotIdByAuctionId.has(ev.auctionId)),
                map((payload) => ok<BidDeletedEvent, Error>({ kind: 'BidDeleted', payload }))
            )
        );

        const events$ = concat(load$, socketEvents$.pipe(skipUntil(ready$)));

        this.eventsPort.startConnections();

        events$.pipe(takeUntil(this.destroy$)).subscribe((eventResult) => {
            if (eventResult.isOk()) {
                this.handleEventSuccess(eventResult.unwrap());
            } else {
                this.handleEventError(eventResult.unwrapErr());
            }
        });

        // Событие смены статуса ТП обрабатываем отдельно от основного потока событий,
        // потому что оно может прийти до того, как снапшот загружен.
        // В таком случае сохраняем его и применяем в onInitLoad.
        this.eventsPort.tradingProcedureStatusChanged$
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => {
                if (!this._tradingProcedure()) {
                    this._pendingStatusEvent = event;
                    return;
                }
                if (this._tradingProcedure()?.id !== event.id) return;
                this._tradingProcedure.update((prev) => (prev ? { ...prev, status: event.status } : prev));
                if (this._role() === 'seller' && SELLER_DEANON_STATUSES.includes(event.status)) {
                    this.loadSellerOrganizationInfos(event.id);
                }
            });

        // switchMap per auctionId — при быстрых событиях подряд отменяет предыдущий запрос,
        // применяется только последний ответ
        this._auctionRefresh$.pipe(
            groupBy((auctionId) => auctionId),
            mergeMap((group$) => group$.pipe(
                switchMap((auctionId) => this.queryPort.getAuctionById(auctionId).pipe(
                    map((result) => ({ auctionId, result }))
                ))
            )),
            takeUntil(this.destroy$)
        ).subscribe(({ auctionId, result }) => {
            if (result.isOk()) this.applyAuctionUpdate(auctionId, result.unwrap());
        });

        this._myBidRefresh$.pipe(
            groupBy((auctionId) => auctionId),
            mergeMap((group$) => group$.pipe(
                switchMap((auctionId) => this.queryPort.getMyBid(auctionId).pipe(
                    map((result) => ({ auctionId, result }))
                ))
            )),
            takeUntil(this.destroy$)
        ).subscribe(({ auctionId, result }) => {
            if (result.isOk()) {
                const myBid = result.unwrap();
                const lotId = this.lotIdByAuctionId.get(auctionId) ?? auctionId;
                const lotRef = this.store.refsByLotId.get(lotId);
                if (lotRef && lotRef.model().kind !== 'Finished') {
                    if (myBid) {
                        this.store.setMyBid(lotId, myBid);
                    } else {
                        this.store.removeMyBid(lotId);
                    }
                }
            }
        });

        this.eventsPort.connectionStatus$.pipe(takeUntil(this.destroy$))
            .subscribe((status) => this._connectionStatus.set(status));

        // При reconnect SignalR — полная ресинхронизация снапшота
        this.eventsPort.reconnected$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                const tp = this._tradingProcedure();
                if (!tp) {
                    return;
                }

                this._status.set('loading');
                this.store.clear();
                this.lotIdByAuctionId.clear();

                this.queryPort
                    .getSnapshot(tp.id)
                    .pipe(take(1))
                    .subscribe((snapshotResult) => {
                        if (snapshotResult.isOk()) {
                            this.handleEventSuccess({ kind: 'InitLoad', snapshot: snapshotResult.unwrap() });
                        } else {
                            this.handleEventError(snapshotResult.unwrapErr());
                        }
                    });
            });
    }

    upsertBid(payload: UpsertSealedBidPayload): Observable<Result<SealedBid, Error>> {
        const lotRef = this.resolveLotRef(payload.auctionId);
        if (!lotRef) {
            return of(err<SealedBid, Error>(new Error($localize`:@@sealed-auction.error.auction-not-found:Auction with id ${payload.auctionId} not found:auctionId:${payload.auctionId}`)));
        }

        const state = lotRef.model();
        if (state.kind === 'Finished') {
            return of(err<SealedBid, Error>(new Error($localize`:@@sealed-auction.error.auction-finished:Auction with id ${payload.auctionId} is finished:auctionId:${payload.auctionId}`)));
        }

        return this.commandPort.upsertBid(payload).pipe(
            map((result) => {
                if (result.isOk()) {
                    const bid = result.unwrap();
                    const lotId = this.lotIdByAuctionId.get(payload.auctionId) ?? payload.auctionId;
                    this.store.setMyBid(lotId, bid);
                }
                return result;
            })
        );
    }

    deleteBid(auctionId: number): Observable<Result<void, Error>> {
        const lotRef = this.resolveLotRef(auctionId);
        if (!lotRef) {
            return of(err<void, Error>(new Error($localize`:@@sealed-auction.error.auction-not-found:Auction with id ${auctionId} not found:auctionId:${auctionId}`)));
        }

        const state = lotRef.model();
        if (state.kind === 'Finished') {
            return of(err<void, Error>(new Error($localize`:@@sealed-auction.error.auction-finished:Auction with id ${auctionId} is finished:auctionId:${auctionId}`)));
        }

        if (!state.myBid) {
            return of(err<void, Error>(new Error($localize`:@@sealed-auction.error.no-bid-found:No bid found for auction ${auctionId}:auctionId:${auctionId}`)));
        }

        const bidId = state.myBid.id;
        const lotId = this.lotIdByAuctionId.get(auctionId) ?? auctionId;

        return this.commandPort.deleteBid(bidId).pipe(
            map((result) => {
                if (result.isOk()) {
                    this.store.removeMyBid(lotId);
                }
                return result;
            })
        );
    }

    getMyBid(auctionId: number): Observable<Result<SealedBid | null, Error>> {
        return this.queryPort.getMyBid(auctionId);
    }

    getTradingProcedureDocuments(): Observable<Result<SealedTradingProcedureDocument[], Error>> {
        return toObservable(this._tradingProcedure).pipe(
            filter((tp) => tp !== null),
            switchMap((tp) => this.queryPort.getTradingProcedureDocuments(tp.id))
        );
    }

    getTradingProcedureRegistry(): Observable<Result<SealedTradingProcedureRegistry, Error>> {
        return toObservable(this._tradingProcedure).pipe(
            filter((tp) => tp !== null),
            switchMap((tp) => this.queryPort.getTradingProcedureRegistry(tp.id))
        );
    }

    downloadRegistryFile(tradingProcedureId: number): Observable<Result<Blob, Error>> {
        return this.queryPort.getRegistryFile(tradingProcedureId);
    }

    dispose(): void {
        this.destroy$.next();
        this.eventsPort.stopConnections();
        this.store.clear();
        this.lotIdByAuctionId.clear();
        this._pendingLeadingBidId.clear();
        this._pendingStatusEvent = null;
    }

    private handleEventError(error: Error): void {
        this._status.set('error');
    }

    private handleEventSuccess(event: SealedTPEvent): void {
        switch (event.kind) {
            case 'InitLoad':
                this.onInitLoad(event);
                break;
            case 'AuctionStarted':
                this.onAuctionStarted(event);
                break;
            case 'AuctionEnded':
                this.onAuctionEnded(event);
                break;
            case 'BidPlaced':
                this.onBidPlaced(event);
                break;
            case 'BidUpdated':
                this.onBidUpdated(event);
                break;
            case 'BidDeleted':
                this.onBidDeleted(event);
                break;
            default:
                exhaustiveCheck(event);
        }

        this._status.set('ok');
    }

    private onInitLoad(event: InitLoadEvent): void {
        const { tradingProcedure, lots, auctions, organizationId, organizationName, role, myBids } = event.snapshot;

        this._organizationId.set(organizationId);
        this._role.set(role);
        if (organizationName) {
            this._organizationNameById.set({ [organizationId]: organizationName });
        }
        this._tradingProcedure.set(tradingProcedure);

        // RC3: применяем накопленное событие статуса если пришло до загрузки снапшота
        if (this._pendingStatusEvent && this._pendingStatusEvent.id === tradingProcedure.id) {
            const pending = this._pendingStatusEvent;
            this._pendingStatusEvent = null;
            this._tradingProcedure.update((prev) => prev ? { ...prev, status: pending.status } : prev);
            if (role === 'seller' && SELLER_DEANON_STATUSES.includes(pending.status)) {
                this.loadSellerOrganizationInfos(tradingProcedure.id);
            }
        } else {
            this._pendingStatusEvent = null;
            if (role === 'seller' && SELLER_DEANON_STATUSES.includes(tradingProcedure.status)) {
                this.loadSellerOrganizationInfos(tradingProcedure.id);
            }
        }

        this.lotIdByAuctionId.clear();

        // Sealed auction стартует сразу после публикации (нет антиснайпинга)
        const tpStartDate = tradingProcedure.startDateTime;
        const tpEndDate = tradingProcedure.endDateTime;

        this.store.upsertLots(lots, tpStartDate, tpEndDate);

        // Set myBids BEFORE processing auction statuses
        // because finishLot copies current.myBid to finished state
        for (const bid of myBids) {
            this.store.setMyBid(bid.lotId, bid);
        }

        // Обновляем состояние лотов на основе статусов аукционов
        for (const auction of auctions) {
            this.lotIdByAuctionId.set(auction.id, auction.lotId);

            switch (auction.status) {
                case 'active':
                    this.store.activateLot({
                        lotId: auction.lotId,
                        bidCount: auction.bidCount,
                        startedAt: tpStartDate,
                        endsAt: tpEndDate,
                    });
                    break;
                case 'finished':
                    this.store.finishLot({
                        lotId: auction.lotId,
                        bidCount: auction.bidCount,
                        finishedAt: tpEndDate,
                        leadingSealedBidId: auction.leadingSealedBidId ?? null,
                    });
                    break;
            }
        }

        this._lotRefsById.set(this.store.refsByLotId);
    }

    private onAuctionStarted(event: AuctionStartedEvent): void {
        // Sealed auction всегда Active при публикации, но событие может прийти для обновления bidCount
        const { auctionId, dateTime, endDateTime } = event.payload;

        // Перезапрашиваем аукцион для получения актуального bidCount
        this.queryPort
            .getAuctionById(auctionId)
            .pipe(take(1))
            .subscribe((result) => {
                if (result.isOk()) {
                    const auction = result.unwrap();
                    const lotId = auction.lotId;
                    this.lotIdByAuctionId.set(auctionId, lotId);

                    const lotRef = this.store.refsByLotId.get(lotId);
                    if (!lotRef) {
                        return;
                    }

                    const state = lotRef.model();
                    if (state.kind === 'Finished') {
                        return;
                    }

                    // Обновляем состояние с актуальным bidCount
                    this.store.activateLot({
                        lotId,
                        bidCount: auction.bidCount,
                        startedAt: dateTime,
                        endsAt: endDateTime ?? state.endsAt,
                    });
                }
            });
    }

    private onAuctionEnded(event: AuctionEndedEvent): void {
        // Сохраняем leadingSealedBidId из события — бэк может его не вернуть в getAuctionById
        const { auctionId, leadingSealedBidId } = event.payload;
        // Храним leadingSealedBidId чтобы applyAuctionUpdate мог его использовать
        if (leadingSealedBidId != null) {
            this._pendingLeadingBidId.set(auctionId, leadingSealedBidId);
        }
        this._auctionRefresh$.next(auctionId);
    }

    private onBidPlaced(event: BidPlacedEvent): void {
        this._auctionRefresh$.next(event.payload.auctionId);
        this._myBidRefresh$.next(event.payload.auctionId);
    }

    private onBidUpdated(event: BidUpdatedEvent): void {
        this._auctionRefresh$.next(event.payload.auctionId);
        this._myBidRefresh$.next(event.payload.auctionId);
    }

    private onBidDeleted(event: BidDeletedEvent): void {
        this._auctionRefresh$.next(event.payload.auctionId);
        this._myBidRefresh$.next(event.payload.auctionId);
    }

    private applyAuctionUpdate(auctionId: number, auction: SealedAuction): void {
        const lotId = auction.lotId;
        this.lotIdByAuctionId.set(auctionId, lotId);

        if (auction.status === 'finished') {
            const leadingSealedBidId = this._pendingLeadingBidId.get(auctionId) ?? auction.leadingSealedBidId ?? null;
            this._pendingLeadingBidId.delete(auctionId);
            this.store.finishLot({ lotId, bidCount: auction.bidCount, leadingSealedBidId });
        } else {
            this.store.setBidCount(lotId, auction.bidCount);
        }
    }

    loadSealedOffers(tradingProcedureId: number): void {
        const query$ = this._role() === 'seller'
            ? this.queryPort.getSealedOffersAsSeller(tradingProcedureId)
            : this.queryPort.getSealedOffersAsBuyer(tradingProcedureId);

        query$.pipe(take(1)).subscribe((result) => {
            if (result.isOk()) {
                const next = new Map<number, SealedOffer[]>();
                for (const offer of result.unwrap()) {
                    const existing = next.get(offer.auctionId) ?? [];
                    next.set(offer.auctionId, [...existing, offer]);
                }
                this._offersByAuctionId.set(next);
            }
        });
    }

    private loadSellerOrganizationInfos(tpId: number): void {
        this.queryPort.getSellerOrganizationInfos(tpId).pipe(take(1)).subscribe((result) => {
            if (result.isOk()) {
                const incoming = Object.fromEntries(
                    result.unwrap()
                        .filter((info) => info.organizationName != null)
                        .map((info) => [info.organizationId, info.organizationName!])
                );
                this._organizationNameById.update((prev) => ({ ...prev, ...incoming }));
            }
        });
    }

    private resolveLotRef(auctionId: number): SealedLotRef | undefined {
        const lotId = this.lotIdByAuctionId.get(auctionId) ?? auctionId;
        return this.store.refsByLotId.get(lotId);
    }
}

